import * as Sentry from '@sentry/react'

// One `fetch()` transport failure, worded differently by every engine, all of them a bare TypeError.
const TRANSPORT_FAILURE_MESSAGES = [
	'Failed to fetch', // Chromium
	'Load failed', // WebKit
	'NetworkError when attempting to fetch resource.', // Gecko
	'Network request failed', // React Native
	// NSURLError's own descriptions, which Apple's networking stack passes to JS verbatim.
	'The network connection was lost.',
	'The Internet connection appears to be offline.',
	'The request timed out.',
]

// NSURLError -999, usually our own navigation: unreported, but never shown as being offline.
const CANCELLED_MESSAGE = 'cancelled'

// Exact, not substring: `Failed to fetch dynamically imported module` has to stay reportable.
export const isOfflineError = (error: unknown): boolean =>
	error instanceof Error && TRANSPORT_FAILURE_MESSAGES.includes(error.message)

export const isNetworkError = (error: unknown): boolean =>
	isOfflineError(error) || (error instanceof Error && error.message === CANCELLED_MESSAGE)

// The query carries `device_udid`, which an issue would then keep for its whole retention window.
const endpointOf = (url: string): string | undefined => {
	try {
		const { origin, pathname } = new URL(url)
		return `${origin}${pathname}`
	} catch {
		return undefined
	}
}

/**
 * A thrown `Response` has no own keys, so it is titled from the call and status instead.
 * The endpoint goes in `extra`, never `cause`: linkedErrors drops a cause that is not an `Error`.
 */
export const captureApiException = (error: unknown, call: string): void => {
	const status = (error as { status?: unknown })?.status

	// Tagged on both branches: here the thrown value keeps its own message, which names no call.
	if (typeof status !== 'number') {
		Sentry.captureException(error, { tags: { call } })
		return
	}

	const url = (error as { url?: unknown })?.url
	const endpoint = typeof url === 'string' && url ? endpointOf(url) : undefined

	Sentry.captureException(new Error(`${call} answered ${status}`, { cause: error }), {
		tags: { call },
		...(endpoint ? { extra: { endpoint } } : {}),
	})
}
